import { useEffect, useMemo, useState } from "react";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import client from "@/lib/api";
import { toast } from "sonner";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildLocalSeoMd } from "@/lib/mdBuilders";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MapPin, Star, RefreshCw, Plus, Trash2, BadgeCheck, Phone, Tag, Upload, Grid3x3, Flame } from "lucide-react";

function HeatmapCell({ cell, isCenter }) {
    const p = cell?.position;
    let bg = "bg-muted-foreground/15 text-muted-foreground"; // not ranked
    let label = "–";
    if (p != null) {
        label = `#${p}`;
        if (p <= 3) bg = "bg-success/80 text-black";
        else if (p <= 10) bg = "bg-primary/70 text-black";
        else if (p <= 20) bg = "bg-warning/70 text-black";
        else bg = "bg-destructive/60 text-black";
    }
    return (
        <div
            data-testid={`heatmap-cell-${cell.row}-${cell.col}`}
            className={`aspect-square flex items-center justify-center text-xs font-mono font-semibold relative ${bg}`}
            title={`${cell.lat.toFixed(4)}, ${cell.lng.toFixed(4)} — ${p != null ? `#${p}` : "not ranked"}`}
        >
            {label}
            {isCenter && (
                <MapPin className="w-3 h-3 absolute top-1 right-1 text-black/70" strokeWidth={2.5} />
            )}
        </div>
    );
}

// Hex colors kept in sync with HeatmapCell tailwind classes so the map
// pins visually match the plain-grid view 1:1.
function cellColorHex(pos) {
    if (pos == null) return "#6b7280";          // muted-foreground
    if (pos <= 3) return "#22c55e";              // success
    if (pos <= 10) return "#38bdf8";             // primary (brand cyan)
    if (pos <= 20) return "#f59e0b";             // warning
    return "#ef4444";                            // destructive
}

function FitBounds({ bounds }) {
    const map = useMap();
    useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds, { padding: [30, 30] });
        }
    }, [bounds, map]);
    return null;
}

function HeatmapMap({ result }) {
    const cells = useMemo(() => (result?.grid || []).flat(), [result]);
    const bounds = useMemo(() => {
        if (!cells.length) return null;
        const lats = cells.map((c) => c.lat);
        const lngs = cells.map((c) => c.lng);
        return [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)],
        ];
    }, [cells]);

    const businessIcon = useMemo(() => L.divIcon({
        className: "",
        html: '<div style="width:14px;height:14px;border-radius:9999px;background:#38bdf8;border:3px solid #0b1220;box-shadow:0 0 0 2px #38bdf8"></div>',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
    }), []);

    if (!result) return null;

    // Cell marker radius scales with grid size so bigger grids don't overlap
    const cellRadius = result.grid_size === 3 ? 24 : result.grid_size === 5 ? 18 : 14;

    return (
        <div className="h-96 w-full border border-border" data-testid="heatmap-map">
            <MapContainer
                center={[result.center.lat, result.center.lng]}
                zoom={13}
                scrollWheelZoom
                style={{ height: "100%", width: "100%", background: "#0b1220" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds bounds={bounds} />
                {cells.map((c) => (
                    <CircleMarker
                        key={`${c.row}-${c.col}`}
                        center={[c.lat, c.lng]}
                        radius={cellRadius}
                        pathOptions={{
                            color: "#0b1220",
                            weight: 2,
                            fillColor: cellColorHex(c.position),
                            fillOpacity: 0.85,
                        }}
                    >
                        <Tooltip direction="center" permanent className="heatmap-cell-tooltip">
                            <span style={{ color: "#000", fontWeight: 700, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                                {c.position != null ? `#${c.position}` : "–"}
                            </span>
                        </Tooltip>
                        <Popup>
                            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                                <div><strong>{c.position != null ? `Rank #${c.position}` : "Not ranked"}</strong></div>
                                {c.matched_title && <div style={{ marginTop: 4, color: "#555" }}>matched: {c.matched_title}</div>}
                                <div style={{ marginTop: 4, color: "#777" }}>{c.lat.toFixed(4)}, {c.lng.toFixed(4)}</div>
                            </div>
                        </Popup>
                    </CircleMarker>
                ))}
                <Marker position={[result.center.lat, result.center.lng]} icon={businessIcon}>
                    <Popup>
                        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                            <strong>{result.center.name}</strong>
                        </div>
                    </Popup>
                </Marker>
            </MapContainer>
        </div>
    );
}

function HeatmapLegend() {
    const stops = [
        { bg: "bg-success/80", label: "Top 3" },
        { bg: "bg-primary/70", label: "4–10" },
        { bg: "bg-warning/70", label: "11–20" },
        { bg: "bg-destructive/60", label: "21+" },
        { bg: "bg-muted-foreground/15", label: "not ranked" },
    ];
    return (
        <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono text-muted-foreground">
            {stops.map((s) => (
                <div key={s.label} className="inline-flex items-center gap-1.5">
                    <span className={`inline-block w-3 h-3 ${s.bg}`} /> {s.label}
                </div>
            ))}
        </div>
    );
}

function HeatmapCompareCell({ a, b }) {
    // a = older run cell, b = newer run cell (current `result`)
    const pa = a?.position;
    const pb = b?.position;
    let delta;         // negative = improved (lower rank is better)
    let bg = "bg-muted-foreground/10 text-muted-foreground";
    let label = "—";
    if (pa != null && pb != null) {
        delta = pb - pa;
        if (delta < 0) { bg = "bg-success/70 text-black"; label = `↑${-delta}`; }
        else if (delta > 0) { bg = "bg-destructive/60 text-black"; label = `↓${delta}`; }
        else { bg = "bg-muted text-foreground"; label = "="; }
    } else if (pa == null && pb != null) {
        bg = "bg-success/70 text-black"; label = `new #${pb}`;
    } else if (pa != null && pb == null) {
        bg = "bg-destructive/60 text-black"; label = `lost #${pa}`;
    }
    return (
        <div className={`aspect-square flex items-center justify-center text-[10px] font-mono font-semibold ${bg}`}
            title={`then: ${pa != null ? `#${pa}` : "–"} → now: ${pb != null ? `#${pb}` : "–"}`}
            data-testid={`heatmap-compare-cell-${b?.row}-${b?.col}`}
        >{label}</div>
    );
}

function MiniGrid({ grid, gridSize, testid }) {
    return (
        <div
            className="grid gap-0.5"
            style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
            data-testid={testid}
        >
            {grid.flat().map((cell) => <HeatmapCell key={`${cell.row}-${cell.col}`} cell={cell} isCenter={false} />)}
        </div>
    );
}

function HeatmapComparePanel({ projectId, currentResult }) {
    const [history, setHistory] = useState([]);
    const [compareId, setCompareId] = useState("");
    const [compareRun, setCompareRun] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!projectId || !currentResult?.keyword_id) { setHistory([]); return; }
        client.get(`/projects/${projectId}/local-seo/heatmap/history`, { params: { keyword_id: currentResult.keyword_id, limit: 20 } })
            .then((r) => {
                // Exclude the current (newest) run from the compare picker
                const others = (r.data.runs || []).filter((x) => x.id !== currentResult.id);
                setHistory(others);
            }).catch(() => setHistory([]));
    }, [projectId, currentResult?.keyword_id, currentResult?.id]);

    const loadCompare = async (runId) => {
        setCompareId(runId);
        setCompareRun(null);
        if (!runId) return;
        setLoading(true);
        try {
            const r = await client.get(`/projects/${projectId}/local-seo/heatmap/runs/${runId}`);
            setCompareRun(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load past run");
        } finally {
            setLoading(false);
        }
    };

    if (!history.length) {
        return (
            <div className="border-t border-border p-4 text-xs text-muted-foreground" data-testid="heatmap-compare-empty">
                No prior heatmap runs for "{currentResult.keyword}" yet — this is your first. Future runs will show week-over-week deltas here.
            </div>
        );
    }

    const canCompare = compareRun && compareRun.grid_size === currentResult.grid_size;
    const deltaAvg = canCompare && compareRun.summary.avg_position != null && currentResult.summary.avg_position != null
        ? +(currentResult.summary.avg_position - compareRun.summary.avg_position).toFixed(1) : null;
    const deltaRanked = canCompare ? currentResult.summary.ranked_cells - compareRun.summary.ranked_cells : null;

    return (
        <div className="border-t border-border p-4 space-y-4" data-testid="heatmap-compare">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">Compare over time</div>
                    <div className="text-xs text-muted-foreground mt-1">Pick a prior run to see geo-visibility shift at every cell.</div>
                </div>
                <select
                    data-testid="heatmap-compare-select"
                    value={compareId}
                    onChange={(e) => loadCompare(e.target.value)}
                    className="bg-input border border-border px-3 py-2 text-xs font-mono focus:outline-none focus:border-primary min-w-[280px]"
                >
                    <option value="">Compare to prior run…</option>
                    {history.map((h) => (
                        <option key={h.id} value={h.id}>
                            {new Date(h.generated_at).toLocaleString()} · {h.grid_size}×{h.grid_size} · {h.radius_miles}mi · avg {h.summary.avg_position ?? "–"}
                        </option>
                    ))}
                </select>
            </div>

            {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

            {compareRun && !canCompare && (
                <div className="border border-warning/40 bg-warning/5 p-3 text-xs text-warning" data-testid="heatmap-compare-mismatch">
                    Grid sizes differ ({compareRun.grid_size}×{compareRun.grid_size} vs {currentResult.grid_size}×{currentResult.grid_size}) — cells can't be compared 1:1. Re-run at a matching grid size.
                </div>
            )}

            {canCompare && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono" data-testid="heatmap-compare-summary">
                        <div><div className="text-muted-foreground">Then · ranked</div><div className="text-foreground">{compareRun.summary.ranked_cells}/{compareRun.summary.total_cells}</div></div>
                        <div><div className="text-muted-foreground">Now · ranked</div><div className="text-foreground">{currentResult.summary.ranked_cells}/{currentResult.summary.total_cells} <span className={deltaRanked > 0 ? "text-success" : deltaRanked < 0 ? "text-destructive" : "text-muted-foreground"}>({deltaRanked > 0 ? "+" : ""}{deltaRanked})</span></div></div>
                        <div><div className="text-muted-foreground">Avg Δ</div><div className={deltaAvg == null ? "text-muted-foreground" : deltaAvg < 0 ? "text-success" : deltaAvg > 0 ? "text-destructive" : ""}>{deltaAvg == null ? "—" : (deltaAvg < 0 ? `↑ ${Math.abs(deltaAvg)}` : deltaAvg > 0 ? `↓ ${deltaAvg}` : "flat")}</div></div>
                        <div><div className="text-muted-foreground">Top-3 Δ</div><div className={currentResult.summary.top3_cells >= compareRun.summary.top3_cells ? "text-success" : "text-destructive"}>{compareRun.summary.top3_cells} → {currentResult.summary.top3_cells}</div></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Then · {new Date(compareRun.generated_at).toLocaleDateString()}</div>
                            <MiniGrid grid={compareRun.grid} gridSize={compareRun.grid_size} testid="heatmap-compare-then" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Delta per cell</div>
                            <div
                                className="grid gap-0.5"
                                style={{ gridTemplateColumns: `repeat(${currentResult.grid_size}, minmax(0, 1fr))` }}
                                data-testid="heatmap-compare-delta"
                            >
                                {currentResult.grid.flat().map((cell) => {
                                    const prior = compareRun.grid[cell.row]?.[cell.col];
                                    return <HeatmapCompareCell key={`d-${cell.row}-${cell.col}`} a={prior} b={cell} />;
                                })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Now · {new Date(currentResult.generated_at).toLocaleDateString()}</div>
                            <MiniGrid grid={currentResult.grid} gridSize={currentResult.grid_size} testid="heatmap-compare-now" />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function HeatmapCompetitorPanel({ projectId, currentResult, locationName }) {
    const [query, setQuery] = useState("");
    const [candidates, setCandidates] = useState(null);
    const [picked, setPicked] = useState(null);
    const [running, setRunning] = useState(false);
    const [compResult, setCompResult] = useState(null);

    const cost = currentResult.grid_size * currentResult.grid_size;

    const search = async () => {
        const q = query.trim();
        if (!q) return toast.error("Enter a competitor name.");
        if (!locationName) return toast.error("Save your business location first.");
        try {
            const r = await client.post(`/projects/${projectId}/local-seo/profile/search`, { query: q, location_name: locationName });
            setCandidates(r.data.candidates || []);
            if (!(r.data.candidates || []).length) toast.error("No listings matched.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't search Maps");
        }
    };

    const run = async () => {
        if (!picked) return toast.error("Pick a competitor listing first.");
        setRunning(true);
        setCompResult(null);
        try {
            const r = await client.post(`/projects/${projectId}/local-seo/heatmap/competitor`, {
                keyword_id: currentResult.keyword_id,
                grid_size: currentResult.grid_size,
                radius_miles: currentResult.radius_miles,
                competitor_name: picked.name,
                competitor_place_id: picked.place_id || null,
                competitor_label: picked.name,
            });
            setCompResult(r.data);
            const s = r.data.summary;
            toast.success(`Competitor heatmap · ${s.ranked_cells}/${s.total_cells} cells${s.avg_position ? ` · avg #${s.avg_position}` : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't run competitor heatmap");
        } finally {
            setRunning(false);
        }
    };

    const stealWins = compResult && currentResult
        ? currentResult.grid.flat().filter((c, i) => {
            const cc = compResult.grid.flat()[i];
            return c.position != null && (cc?.position == null || c.position < cc.position);
        }).length
        : 0;
    const losses = compResult && currentResult
        ? compResult.grid.flat().filter((c, i) => {
            const my = currentResult.grid.flat()[i];
            return c.position != null && (my?.position == null || c.position < my.position);
        }).length
        : 0;

    return (
        <div className="border-t border-border p-4 space-y-4" data-testid="heatmap-competitor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">Competitor heatmap</div>
                    <div className="text-xs text-muted-foreground mt-1">Same grid, same keyword — matched against a competitor's listing. See exactly where you win and where they beat you.</div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                <input
                    data-testid="competitor-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Competitor business name (e.g. Blue Bottle Coffee)"
                    className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                />
                <CreditActionButton
                    actionKey="local_business_refresh"
                    testid="competitor-search-btn"
                    onConfirm={search}
                    description="Searches Google Maps for the competitor listing — pick the right one."
                >
                    Search competitor
                </CreditActionButton>
            </div>

            {candidates && candidates.length > 0 && (
                <div className="space-y-2" data-testid="competitor-candidates">
                    {candidates.slice(0, 5).map((c, i) => (
                        <label key={c.place_id || `${c.name}-${i}`} className={`flex items-start justify-between gap-3 border p-2 cursor-pointer text-xs ${picked?.place_id === c.place_id ? "border-primary" : "border-border/60 hover:border-muted-foreground"}`}>
                            <div className="min-w-0">
                                <div className="font-medium">{c.name} {c.rating != null && <span className="text-muted-foreground">· {c.rating}★ ({c.reviews_count})</span>}</div>
                                {c.address && <div className="text-muted-foreground truncate">{c.address}</div>}
                            </div>
                            <input
                                type="radio"
                                name="competitor-pick"
                                data-testid="competitor-candidate-radio"
                                checked={picked?.place_id === c.place_id}
                                onChange={() => setPicked(c)}
                            />
                        </label>
                    ))}
                </div>
            )}

            {picked && (
                <CreditActionButton
                    actionKey="local_heatmap_cell"
                    costMultiplier={cost}
                    testid="competitor-run-btn"
                    onConfirm={run}
                    disabled={running}
                    description={`Runs ${currentResult.grid_size}×${currentResult.grid_size} = ${cost} real Google Maps SERP calls (${cost} credits) matching against "${picked.name}".`}
                >
                    {running ? "Running…" : `Run competitor heatmap (${cost} cr)`}
                </CreditActionButton>
            )}

            {compResult && (
                <div className="space-y-3" data-testid="competitor-result">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono">
                        <div><div className="text-muted-foreground">You · ranked</div><div className="text-foreground">{currentResult.summary.ranked_cells}/{currentResult.summary.total_cells}</div></div>
                        <div><div className="text-muted-foreground">Them · ranked</div><div className="text-foreground">{compResult.summary.ranked_cells}/{compResult.summary.total_cells}</div></div>
                        <div><div className="text-muted-foreground">Cells you win</div><div className="text-success">{stealWins}</div></div>
                        <div><div className="text-muted-foreground">Cells they win</div><div className="text-destructive">{losses}</div></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">You · {currentResult.center.name}</div>
                            <MiniGrid grid={currentResult.grid} gridSize={currentResult.grid_size} testid="competitor-you-grid" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wider">Them · {compResult.competitor_label}</div>
                            <MiniGrid grid={compResult.grid} gridSize={compResult.grid_size} testid="competitor-them-grid" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function HeatmapPanel({ projectId, rows, result, running, onRun, locationName }) {
    const [kwId, setKwId] = useState("");
    const [gridSize, setGridSize] = useState(5);
    const [radius, setRadius] = useState(5);
    const [view, setView] = useState("map");

    // Group tracked keywords: top-20 by best position, bottom-20 by worst/unranked
    const ranked = rows
        .filter((r) => r.position != null)
        .sort((a, b) => a.position - b.position);
    const unranked = rows.filter((r) => r.position == null);
    const top20 = ranked.slice(0, 20);
    const bottomFromRanked = [...ranked].reverse().slice(0, Math.max(0, 20 - unranked.length));
    const bottom20 = [...unranked, ...bottomFromRanked].slice(0, 20);

    const cost = gridSize * gridSize;

    const run = () => {
        if (!kwId) return toast.error("Pick a keyword to heatmap.");
        if (radius < 1 || radius > 25) return toast.error("Radius must be between 1 and 25 miles.");
        onRun({ keyword_id: kwId, grid_size: gridSize, radius_miles: Number(radius) });
    };

    return (
        <div className="border border-border bg-card/40" data-testid="heatmap-section">
            <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="overline inline-flex items-center gap-2"><Flame className="w-3.5 h-3.5" /> Map pack heatmap</div>
                    <div className="text-xs text-muted-foreground mt-1">Run a geo-grid of real Google Maps SERPs around your business — one query per grid cell.</div>
                </div>
            </div>

            <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
                <div>
                    <label className="text-[10px] font-mono text-muted-foreground block mb-1">Keyword</label>
                    <select
                        data-testid="heatmap-keyword-select"
                        value={kwId}
                        onChange={(e) => setKwId(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                    >
                        <option value="">Pick a tracked local keyword…</option>
                        {top20.length > 0 && (
                            <optgroup label={`Top performers (ranking best) · ${top20.length}`}>
                                {top20.map((r) => (
                                    <option key={r.id} value={r.id}>{r.keyword} · #{r.position}</option>
                                ))}
                            </optgroup>
                        )}
                        {bottom20.length > 0 && (
                            <optgroup label={`Needs improvement (worst / not ranked) · ${bottom20.length}`}>
                                {bottom20.map((r) => (
                                    <option key={`b-${r.id}`} value={r.id}>{r.keyword} · {r.position != null ? `#${r.position}` : "not ranked"}</option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-mono text-muted-foreground block mb-1">Grid</label>
                    <div className="inline-flex border border-border" data-testid="heatmap-grid-picker">
                        {[3, 5, 7].map((n) => (
                            <button
                                key={n}
                                type="button"
                                data-testid={`heatmap-grid-${n}`}
                                onClick={() => setGridSize(n)}
                                className={`px-3 py-2 text-xs font-mono ${gridSize === n ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                            >
                                {n}×{n}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-mono text-muted-foreground block mb-1">Radius (mi)</label>
                    <input
                        data-testid="heatmap-radius-input"
                        type="number"
                        min={1}
                        max={25}
                        step={0.5}
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                        className="w-24 bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                    />
                </div>
                <div>
                    <CreditActionButton
                        actionKey="local_heatmap_cell"
                        costMultiplier={cost}
                        testid="heatmap-run-btn"
                        onConfirm={run}
                        disabled={!kwId || running}
                        description={`Runs ${gridSize}×${gridSize} = ${cost} real Google Maps SERP calls (${cost} credits) at ${radius} mi around your business.`}
                    >
                        <span className="inline-flex items-center gap-1">
                            <Grid3x3 className="w-3.5 h-3.5" /> {running ? "Running…" : `Run heatmap (${cost} cr)`}
                        </span>
                    </CreditActionButton>
                </div>
            </div>

            {result && (
                <div className="border-t border-border p-4 space-y-4" data-testid="heatmap-result">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-sm font-medium">"{result.keyword}" · {result.radius_miles} mi · {result.grid_size}×{result.grid_size}</div>
                            <div className="text-xs text-muted-foreground mt-1">{result.center.name} · {result.center.lat.toFixed(4)}, {result.center.lng.toFixed(4)}</div>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="inline-flex border border-border" data-testid="heatmap-view-toggle">
                                {[{ k: "map", label: "Map" }, { k: "grid", label: "Grid" }].map((v) => (
                                    <button
                                        key={v.k}
                                        type="button"
                                        data-testid={`heatmap-view-${v.k}`}
                                        onClick={() => setView(v.k)}
                                        className={`px-3 py-1.5 text-xs font-mono ${view === v.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                            <div className="grid grid-cols-4 gap-x-6 gap-y-1 text-xs font-mono" data-testid="heatmap-summary">
                                <div><div className="text-muted-foreground">Ranked</div><div className="text-foreground">{result.summary.ranked_cells}/{result.summary.total_cells}</div></div>
                                <div><div className="text-muted-foreground">Avg pos</div><div className="text-foreground">{result.summary.avg_position ?? "—"}</div></div>
                                <div><div className="text-muted-foreground">Top 3</div><div className="text-success">{result.summary.top3_cells}</div></div>
                                <div><div className="text-muted-foreground">Top 10</div><div className="text-primary">{result.summary.top10_cells}</div></div>
                            </div>
                        </div>
                    </div>

                    {view === "map" ? (
                        <HeatmapMap result={result} />
                    ) : (
                        <div
                            className="grid gap-1 mx-auto"
                            style={{ gridTemplateColumns: `repeat(${result.grid_size}, minmax(0, 1fr))`, maxWidth: `${Math.min(560, result.grid_size * 90)}px` }}
                            data-testid="heatmap-grid"
                        >
                            {result.grid.flat().map((cell) => {
                                const mid = Math.floor(result.grid_size / 2);
                                return <HeatmapCell key={`${cell.row}-${cell.col}`} cell={cell} isCenter={cell.row === mid && cell.col === mid} />;
                            })}
                        </div>
                    )}

                    <HeatmapLegend />
                </div>
            )}
            {result && <HeatmapComparePanel projectId={projectId} currentResult={result} />}
            {result && <HeatmapCompetitorPanel projectId={projectId} currentResult={result} locationName={locationName} />}
        </div>
    );
}

function BusinessProfileCard({ info }) {
    const dist = info.rating_distribution || {};
    const maxCount = Math.max(1, ...Object.values(dist).map((v) => Number(v) || 0));
    return (
        <div className="border border-border bg-card/40 p-5" data-testid="business-info-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-display text-xl tracking-tight">{info.name}</h3>
                        {info.is_claimed && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-success/60 text-success px-1.5 py-0.5" data-testid="business-claimed-badge">
                                <BadgeCheck className="w-3 h-3" /> Claimed
                            </span>
                        )}
                    </div>
                    {info.category && <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {info.category}</div>}
                </div>
                {info.rating != null && (
                    <div className="text-right" data-testid="business-rating">
                        <div className="text-2xl font-display flex items-center gap-1 justify-end">
                            <Star className="w-4 h-4 text-primary fill-primary" /> {info.rating}
                        </div>
                        <div className="text-xs text-muted-foreground">{(info.reviews_count || 0).toLocaleString()} reviews</div>
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
                {info.address && <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {info.address}</div>}
                {info.phone && <div className="flex items-start gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {info.phone}</div>}
            </div>
            {Object.keys(dist).length > 0 && (
                <div className="mt-4 space-y-1" data-testid="rating-distribution">
                    {[5, 4, 3, 2, 1].map((star) => (
                        <div key={star} className="flex items-center gap-2 text-xs font-mono">
                            <span className="w-3 text-muted-foreground">{star}★</span>
                            <div className="flex-1 h-1.5 bg-border/60">
                                <div className="h-1.5 bg-primary" style={{ width: `${(100 * (Number(dist[star]) || 0)) / maxCount}%` }} />
                            </div>
                            <span className="w-8 text-right text-muted-foreground">{dist[star] || 0}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LocalSEO() {
    const project = useProject();
    const [profileData, setProfileData] = useState(null);
    const [keywordsData, setKeywordsData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [searchForm, setSearchForm] = useState({ query: "", location_name: "" });
    const [candidates, setCandidates] = useState(null); // null = not searched; [] = no results
    const [selecting, setSelecting] = useState(null);   // place_id currently being saved
    const [newKw, setNewKw] = useState("");
    const [newLoc, setNewLoc] = useState("");
    const [batchText, setBatchText] = useState("");
    const [batchOpen, setBatchOpen] = useState(false);
    const [heatmapResult, setHeatmapResult] = useState(null);
    const [heatmapRunning, setHeatmapRunning] = useState(false);

    const load = () => {
        if (!project) return;
        client.get(`/projects/${project.id}/local-seo/profile`).then((r) => {
            setProfileData(r.data);
            setSearchForm({
                query: r.data.profile?.business_name || "",
                location_name: r.data.profile?.location_name || "",
            });
            if (!newLoc) setNewLoc(r.data.profile?.location_name || "");
        });
        client.get(`/projects/${project.id}/local-seo/keywords`).then((r) => setKeywordsData(r.data));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [project]);

    const searchBusiness = async () => {
        const query = searchForm.query.trim();
        const loc = searchForm.location_name.trim();
        if (!query || !loc) {
            toast.error("Business name and location are required.");
            return;
        }
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/profile/search`, { query, location_name: loc });
            setCandidates(r.data.candidates || []);
            if (!(r.data.candidates || []).length) {
                toast.error("No listings found. Try a shorter name or a different location.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't search Google Maps");
        }
    };

    const selectCandidate = async (c) => {
        setSelecting(c.place_id || c.name);
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/profile/select`, {
                ...c,
                location_name: searchForm.location_name.trim(),
            });
            setProfileData((d) => ({
                ...d,
                profile: r.data.profile,
                business_info: r.data.business_info,
                cached_at: r.data.cached_at,
            }));
            setNewLoc(r.data.profile.location_name || "");
            setCandidates(null);
            setEditing(false);
            toast.success(`Connected to ${r.data.profile.business_name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save the picked listing");
        } finally {
            setSelecting(null);
        }
    };

    const refreshProfile = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/profile/refresh`);
            setProfileData((d) => ({ ...d, business_info: r.data.business_info, cached_at: r.data.cached_at }));
            toast.success("Pulled live Google Business Profile data");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't pull business profile");
        }
    };

    const addKeyword = async () => {
        const kw = newKw.trim().toLowerCase();
        const loc = newLoc.trim();
        if (!kw || !loc) {
            toast.error("Enter a keyword and location.");
            return;
        }
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/keywords`, { keyword: kw, location_name: loc });
            setKeywordsData((d) => ({ ...d, keywords: [...(d?.keywords || []), r.data] }));
            setNewKw("");
            toast.success(`Tracking "${kw}" in ${loc}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't add local keyword");
        }
    };

    const addBatch = async () => {
        const kws = Array.from(new Set(batchText.split(/\n+/).map((k) => k.trim().toLowerCase()).filter(Boolean))).slice(0, 50);
        if (!kws.length) {
            toast.error("Paste at least one keyword.");
            return;
        }
        if (!newLoc.trim()) {
            toast.error("Enter a location first.");
            return;
        }
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/keywords/batch`, { keywords: kws, location_name: newLoc.trim() });
            setKeywordsData((d) => ({ ...d, keywords: [...(d?.keywords || []), ...(r.data.keywords || [])] }));
            setBatchText("");
            setBatchOpen(false);
            toast.success(`Added ${r.data.added} keyword${r.data.added === 1 ? "" : "s"}${r.data.skipped_duplicates ? ` · skipped ${r.data.skipped_duplicates} duplicates` : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Batch add failed");
        }
    };

    const removeKeyword = async (id) => {
        try {
            await client.delete(`/local-seo/keywords/${id}`);
            setKeywordsData((d) => ({ ...d, keywords: (d?.keywords || []).filter((k) => k.id !== id) }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete");
        }
    };

    const refreshAll = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/keywords/refresh`);
            setKeywordsData((d) => ({ ...d, keywords: r.data.keywords }));
            toast.success(`Refreshed ${r.data.refreshed} local keyword${r.data.refreshed === 1 ? "" : "s"}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Refresh failed");
        }
    };

    const runHeatmap = async (payload) => {
        setHeatmapRunning(true);
        setHeatmapResult(null);
        try {
            const r = await client.post(`/projects/${project.id}/local-seo/heatmap`, payload);
            setHeatmapResult(r.data);
            const s = r.data.summary;
            toast.success(`Heatmap ready · ${s.ranked_cells}/${s.total_cells} cells ranked${s.avg_position ? ` · avg #${s.avg_position}` : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Heatmap failed");
        } finally {
            setHeatmapRunning(false);
        }
    };

    if (!project || !profileData || !keywordsData) {
        return <div className="text-muted-foreground font-mono text-sm">loading local seo…</div>;
    }

    const profile = profileData.profile || {};
    const hasProfile = !!profile.business_name;
    const rows = keywordsData.keywords || [];

    return (
        <div className="space-y-6" data-testid="local-seo-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Local SEO toolkit</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Local SEO</h1>
                    <p className="text-sm text-muted-foreground mt-2">Google Business Profile insights and map-pack rank tracking.</p>
                </div>
                <DownloadMdButton
                    filenamePrefix={`inphase-local-seo-${project?.domain || "project"}`}
                    buildMarkdown={() => buildLocalSeoMd({ project, profileData, keywordsData })}
                    disabled={!hasProfile}
                    testId="download-local-seo-md"
                />
            </div>

            {!profileData.dfs_live && (
                <div className="border border-border bg-card/40 p-6 text-sm text-muted-foreground" data-testid="local-seo-not-configured">
                    Local SEO needs a live DataForSEO connection configured by your admin.
                </div>
            )}

            {/* Business profile */}
            <div className="border border-border bg-card/40 p-5" data-testid="business-profile-section">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline">Business profile</div>
                    {hasProfile && !editing && (
                        <div className="flex items-center gap-2">
                            <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="text-xs text-muted-foreground hover:text-foreground">Edit</button>
                            <CreditActionButton
                                actionKey="local_business_refresh"
                                testid="refresh-business-info-btn"
                                variant="outline"
                                onConfirm={refreshProfile}
                                description="Pulls your real Google Business Profile (rating, reviews, category, address, claimed status) from DataForSEO's Business Data API."
                            >
                                <span className="inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh profile</span>
                            </CreditActionButton>
                        </div>
                    )}
                </div>

                {(!hasProfile || editing) ? (
                    <div className="space-y-4" data-testid="profile-picker">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                            <input
                                data-testid="profile-search-query-input"
                                value={searchForm.query}
                                onChange={(e) => setSearchForm((f) => ({ ...f, query: e.target.value }))}
                                placeholder="Business name (e.g. Joe's Pizza)"
                                className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                            />
                            <input
                                data-testid="profile-search-location-input"
                                value={searchForm.location_name}
                                onChange={(e) => setSearchForm((f) => ({ ...f, location_name: e.target.value }))}
                                placeholder="Location (e.g. Austin,Texas,United States)"
                                className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                            />
                            <CreditActionButton
                                actionKey="local_business_refresh"
                                testid="profile-search-btn"
                                onConfirm={searchBusiness}
                                description="Searches Google Maps and shows real matching business listings — pick the exact one that's yours."
                            >
                                Search Google
                            </CreditActionButton>
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                            Location must be DataForSEO format, e.g. "Austin,Texas,United States" or "Berlin,Berlin,Germany".
                        </div>

                        {candidates && candidates.length > 0 && (
                            <div className="border-t border-border pt-4 space-y-2" data-testid="profile-candidates-list">
                                <div className="overline text-xs mb-1">Pick your listing · {candidates.length} match{candidates.length === 1 ? "" : "es"}</div>
                                {candidates.map((c, i) => (
                                    <div
                                        key={c.place_id || `${c.name}-${i}`}
                                        className="flex items-start justify-between gap-3 border border-border/60 p-3 hover:border-primary/60 transition-colors"
                                        data-testid="profile-candidate-row"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{c.name}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                {c.rating != null && (
                                                    <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-primary fill-primary" /> {c.rating} <span className="text-muted-foreground/70">({(c.reviews_count || 0).toLocaleString()})</span></span>
                                                )}
                                                {c.category && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {c.category}</span>}
                                                {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                                            </div>
                                            {c.address && <div className="text-xs text-muted-foreground mt-1 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {c.address}</div>}
                                        </div>
                                        <button
                                            type="button"
                                            data-testid="select-candidate-btn"
                                            onClick={() => selectCandidate(c)}
                                            disabled={!!selecting}
                                            className="shrink-0 bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {selecting === (c.place_id || c.name) ? "Saving…" : "Use this listing"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {candidates && candidates.length === 0 && (
                            <div className="border border-border/60 p-6 text-center text-sm text-muted-foreground" data-testid="profile-candidates-empty">
                                No Google Maps listings matched. Try a shorter name or widen the location.
                            </div>
                        )}
                        {editing && hasProfile && (
                            <button type="button" data-testid="cancel-edit-profile-btn" onClick={() => { setEditing(false); setCandidates(null); }} className="border border-border px-4 py-2 text-sm hover:border-muted-foreground">Cancel</button>
                        )}
                    </div>
                ) : profileData.business_info ? (
                    <BusinessProfileCard info={profileData.business_info} />
                ) : (
                    <div className="border border-border/60 p-8 text-center" data-testid="business-info-empty">
                        <MapPin className="w-7 h-7 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
                        <div className="text-sm text-muted-foreground mb-4">No live Google Business Profile data pulled yet for "{profile.business_name}".</div>
                        <CreditActionButton
                            actionKey="local_business_refresh"
                            testid="pull-business-info-btn"
                            onConfirm={refreshProfile}
                            description="Pulls your real Google Business Profile (rating, reviews, category, address, claimed status) from DataForSEO's Business Data API."
                        >
                            <span className="inline-flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Pull business profile</span>
                        </CreditActionButton>
                    </div>
                )}
            </div>

            {/* Local keywords */}
            {hasProfile && (
                <div className="border border-border bg-card/40" data-testid="local-keywords-section">
                    <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="overline">Map pack rankings</div>
                            <div className="text-xs text-muted-foreground mt-1">{rows.length} local keyword{rows.length === 1 ? "" : "s"} tracked for {profile.business_name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button data-testid="open-local-batch-btn" onClick={() => setBatchOpen((v) => !v)} className="border border-border px-3 py-2 text-xs font-mono hover:border-primary hover:text-primary flex items-center gap-2">
                                <Upload className="w-3.5 h-3.5" /> Batch add
                            </button>
                            {rows.length > 0 && (
                                <CreditActionButton
                                    actionKey="local_rank_refresh"
                                    costMultiplier={rows.length}
                                    testid="refresh-local-keywords-btn"
                                    variant="outline"
                                    onConfirm={refreshAll}
                                    description={`Pulls fresh Google Maps rankings for all ${rows.length} tracked local keyword${rows.length === 1 ? "" : "s"}.`}
                                >
                                    <span className="inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh all</span>
                                </CreditActionButton>
                            )}
                        </div>
                    </div>

                    <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
                        <input
                            data-testid="new-local-keyword-input"
                            value={newKw}
                            onChange={(e) => setNewKw(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                            placeholder="e.g. pizza near me"
                            className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary flex-1 min-w-[180px]"
                        />
                        <input
                            data-testid="new-local-keyword-location-input"
                            value={newLoc}
                            onChange={(e) => setNewLoc(e.target.value)}
                            placeholder="Location, e.g. Austin,Texas,United States"
                            className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary flex-1 min-w-[220px]"
                        />
                        <button data-testid="add-local-keyword-btn" onClick={addKeyword} className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Add
                        </button>
                    </div>

                    {batchOpen && (
                        <div className="p-4 border-b border-border space-y-2" data-testid="local-batch-panel">
                            <p className="text-xs text-muted-foreground">Paste up to 50 keywords, one per line. They'll all use the location above ("{newLoc || "—"}").</p>
                            <textarea
                                data-testid="local-batch-textarea"
                                value={batchText}
                                onChange={(e) => setBatchText(e.target.value)}
                                rows={5}
                                placeholder={"pizza near me\nbest pizza austin\npizza delivery\n…"}
                                className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary resize-y"
                            />
                            <button data-testid="submit-local-batch-btn" onClick={addBatch} className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90">Add batch</button>
                        </div>
                    )}

                    {rows.length === 0 ? (
                        <div className="p-10 text-center text-sm text-muted-foreground" data-testid="local-keywords-empty">No local keywords tracked yet — add one above to start map-pack rank tracking.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-muted-foreground">
                                    <tr className="border-b border-border">
                                        <th className="text-left p-3">Keyword</th>
                                        <th className="text-left p-3">Location</th>
                                        <th className="text-right p-3">Position</th>
                                        <th className="text-left p-3">Top competitor</th>
                                        <th className="text-right p-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id} className="border-b border-border/50 hover:bg-accent/20" data-testid={`local-keyword-row-${r.id}`}>
                                            <td className="p-3 font-mono">{r.keyword}</td>
                                            <td className="p-3 text-xs text-muted-foreground">{r.location_name}</td>
                                            <td className="p-3 text-right font-mono">
                                                {!r.metrics_available ? (
                                                    <span className="text-muted-foreground text-xs">not pulled</span>
                                                ) : r.position ? (
                                                    <span className={r.position <= 3 ? "text-success" : "text-foreground"}>#{r.position}</span>
                                                ) : (
                                                    <span className="text-warning text-xs">not ranking</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-muted-foreground">
                                                {r.top_competitors?.[0] ? `${r.top_competitors[0].title} (#${r.top_competitors[0].position})` : "—"}
                                            </td>
                                            <td className="p-3 text-right">
                                                <button data-testid={`delete-local-keyword-${r.id}`} onClick={() => removeKeyword(r.id)} className="text-muted-foreground hover:text-destructive">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Map Pack Heatmap */}
            {hasProfile && (
                <HeatmapPanel
                    projectId={project?.id}
                    rows={rows}
                    result={heatmapResult}
                    running={heatmapRunning}
                    onRun={runHeatmap}
                    locationName={profile.location_name}
                />
            )}
        </div>
    );
}
